import React, { useEffect, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import {
  getUserState,
  linkSecondaryAuth,
  registerIndividualUser,
} from "actions/userActions";
import { useAuth0 } from "utils/react-auth0-wrapper";
import { useApolloClient } from "@apollo/client";
import type { ApolloClient } from "@apollo/client";
import { isEmpty } from "lodash";
import { iconIdSelector } from "../store/selectors/auth0";
import queryString from "query-string";
import Alert from "../components/Alert";
import IconSpinner from "../components/IconSpinner";
import type { RootState } from "types/state";
import type { AppDispatch } from "store/configureStore";
import type { RegisterIndividualUserMutationVariables } from "types/graphql";

// Narrowed shape of useAuth0's `appState` (typed `unknown`) as read here.
interface IndividualLoginAppState {
  targetUrl?: string;
  authLinkingSessionId?: string;
}

const mapStateToProps = (state: RootState) => ({
  userRegistered: state.user.userRegistered,
});

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  // Declared on the original propTypes but never read by the component.
  userState?: object;
}

type Props = OwnProps & PropsFromRedux;

const IndividualLogin = ({ dispatch, userRegistered }: Props) => {
  const { user, isAuthenticated, loading, appState, logout } = useAuth0();
  const client = useApolloClient();
  const [registeringUser, setRegisteringUser] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const userHasIconAccount = !isEmpty(iconIdSelector(user));

  /*
   * Users from individual portal are actually either registered through Auth0 directly or through
   * an SSO provider which is also stored in Auth0. If the user has come into the application and
   * is authenticated but has no icon account id we can assume they are a new user and need to push
   * them through the registration flow and then get their newly created user state
   *  */
  useEffect(() => {
    const registerUser = async () => {
      if (isAuthenticated) {
        if (!userHasIconAccount) {
          const loginAppState = appState as IndividualLoginAppState | undefined;
          // Auth0Provider's onRedirectCallback strips query params from the URL
          // after the callback, and App.js's restore-navigate races this effect.
          // Read from appState.targetUrl (the original entry URL stashed by
          // PublicRoute/PrivateRoute before login) so we don't lose params like
          // invitationCode/inviteId on the first effect run.
          const targetUrlSearch = loginAppState?.targetUrl?.includes("?")
            ? loginAppState.targetUrl.slice(
                loginAppState.targetUrl.indexOf("?")
              )
            : "";
          const queryParams = queryString.parse(
            targetUrlSearch || window.location.search
          );
          const { inviteId = "", invitationCode = "" } = queryParams;
          const { authLinkingSessionId } = loginAppState!;
          setRegisteringUser(true);
          if (authLinkingSessionId) {
            try {
              // dispatch is typed as redux Dispatch by connect; cast to
              // AppDispatch (ThunkDispatch) to dispatch the thunk action.
              await (dispatch as AppDispatch)(
                linkSecondaryAuth(
                  client as ApolloClient<object>,
                  authLinkingSessionId
                )
              );
            } catch (err) {
              setError((err as Error).message);
              logout({
                logoutParams: {
                  returnTo: window.location.origin + "/login",
                },
              });
            } finally {
              setRegisteringUser(false);
              window.location.reload();
            }
          } else {
            setError(null);

            try {
              return await (dispatch as AppDispatch)(
                registerIndividualUser(
                  client as ApolloClient<object>,
                  {
                    inviteId,
                    invitationCode,
                    // query-string yields string | null | array; the original
                    // passed the parsed values straight through unchanged.
                  } as RegisterIndividualUserMutationVariables
                )
              );
            } catch (err) {
              setError((err as Error).message);
              logout({
                logoutParams: {
                  returnTo: window.location.origin + "/login",
                },
              });
            } finally {
              setRegisteringUser(false);
            }
          }
        }
      }
    };

    void registerUser();
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && !loading && userRegistered) {
      void (dispatch as AppDispatch)(
        getUserState(client as ApolloClient<object>, user)
      );
    }
  }, [isAuthenticated, loading, user, userRegistered]);

  if (loading || registeringUser) {
    return <IconSpinner centered />;
  }

  if (error) {
    return <Alert type="error" msg={error} />;
  }

  return <div></div>;
};

export default connector(IndividualLogin);
